<template>
  <Dropdown
    v-bind="$attrs"
    :trigger="trigger"
    :manual="manual"
    :open="open"
    :position="placement"
    :content-match-trigger-width="dropdownTriggerWidth"
    @update:open="(v: boolean) => emit('update:open', v)"
    ref="dropdownRef"
    :content-style="
      dropdownTriggerWidth ? { overflow: 'auto' } : { overflow: 'auto', minWidth: '100px' }
    "
  >
    <template #trigger="{ active }">
      <div
        :class="[
          'select-trigger',
          `select-trigger--${variant}`,
          {
            'select-trigger--active': active,
            'select-trigger--disabled': disabled,
            'select-trigger--placeholder': !selectedOption,
          },
        ]"
      >
        <div class="select-trigger__icon-slot">
          <slot name="icon"></slot>
        </div>
        <div class="select-trigger__text">
          {{ selectedOption ? selectedOption.label : placeholder }}
        </div>
        <div class="select-trigger__icon">
          <div
            v-if="selectedOption && clearable"
            class="select-trigger__clear"
            @click.stop="handleClear"
          >
            <IconClear class="select-trigger__clear-icon" />
          </div>
          <div
            :class="[
              'select-trigger__arrow',
              { 'select-trigger__arrow--active': active, 'select-trigger_arrow-show': !clearable },
            ]"
          >
            <IconArrow class="select-trigger__arrow-icon" />
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <div class="select-border">
        <div class="select-content">
          <div
            v-for="option in options"
            :key="option.label"
            :class="[
              'select-option',
              {
                'select-option--selected': option.value === modelValue,
                'select-option--disabled': option.disabled,
              },
            ]"
            @click="handleOptionClick(option)"
          >
            <slot name="option" :option="option">
              {{ option.label }}
            </slot>
          </div>
          <div v-if="!options.length" class="select-empty">
            {{ emptyText }}
          </div>
        </div>
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts" generic="T">
import { computed, ref } from 'vue';
import IconArrow from '../../assets/arrow.svg?component';
import IconClear from '../../assets/circle-error.svg?component';
import { Dropdown } from '../dropdown';
import type { SelectOption, SelectProps } from './types';

const props = withDefaults(defineProps<SelectProps<T>>(), {
  variant: 'default',
  dropdownTriggerWidth: false,
  placeholder: '请选择',
  emptyText: '暂无数据',
  disabled: false,
  clearable: true,
  trigger: 'hover',
  placement: 'bottomLeft',
  manual: false,
  open: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: T | null];
  'update:open': [value: boolean];
  change: [value: T, option: SelectOption<T>];
  clear: [];
}>();

// Dropdown 引用
const dropdownRef = ref<InstanceType<typeof Dropdown>>();

// 计算当前选中的选项
const selectedOption = computed(() => {
  return props.options.find(option => option.value === props.modelValue);
});

// 处理选项点击
const handleOptionClick = (option: SelectOption<T>) => {
  if (option.disabled) return;
  emit('update:modelValue', option.value);
  emit('change', option.value, option);
  // 选择后关闭下拉菜单
  dropdownRef.value?.close();
};

const handleClear = () => {
  emit('update:modelValue', null);
  emit('clear');
};

defineExpose({
  open: () => dropdownRef.value?.open(),
  close: () => dropdownRef.value?.close(),
  toggle: () => dropdownRef.value?.toggle(),
});
</script>

<style scoped lang="scss">
.select-trigger__arrow-icon,
.select-trigger__clear-icon {
  width: 16px;
  height: 16px;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 32px;
  min-width: 48px;
  width: fit-content;
  padding: 4px 11px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  box-sizing: border-box;
  gap: 4px;
}

.select-trigger--bordered {
  justify-content: space-between;
  width: 100%;
  min-width: 120px;
  border: 1px solid var(--x-back-4);
  background: var(--x-back-1);
  padding: 7px 16px;
  box-sizing: border-box;
  &:hover:not(.select-trigger--disabled) {
    border-color: var(--x-purple-6);
    box-shadow: none;
  }
  &:hover {
    &:not(.select-trigger--placeholder) .select-trigger__arrow {
      display: none;
    }
    .select-trigger_arrow-show {
      display: flex !important;
    }
    .select-trigger__clear {
      display: flex;
    }
  }
  &.select-trigger--active {
    border-color: var(--x-purple-5);
    box-shadow: 0 0 0 2px var(--x-purple-1);
  }
}

.select-trigger--circle {
  background-color: var(--x-back-1);
  border: 1px solid var(--x-back-4);
  border-radius: 16px;
  padding: 4px 11px;
  &:hover:not(.select-trigger--disabled) {
    border-color: var(--x-purple-6);
  }
  &.select-trigger--active {
    background-color: var(--x-back-1);
    border-color: var(--x-purple-5);
  }
}

.select-trigger:not(.select-trigger--bordered):hover {
  &:not(.select-trigger--placeholder) .select-trigger__arrow {
    display: none;
  }
  .select-trigger_arrow-show {
    display: flex !important;
  }
  .select-trigger__clear {
    display: flex;
  }
}

.select-trigger--disabled {
  cursor: not-allowed;
  background-color: var(--x-back-4);
  color: var(--x-text-2);
}

.select-trigger--disabled:hover {
  background-color: var(--x-back-4);
}

.select-trigger--placeholder .select-trigger__text {
  color: var(--x-text-5);
  font-weight: var(--x-weight-regular);
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
}

.select-trigger__text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--x-text-1);
  font-weight: var(--x-weight-regular);
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
}

.select-trigger__icon-slot {
  // display: flex;
  // align-items: center;
  margin-right: 4px;
}

.select-trigger__icon {
  margin-left: 4px;
  width: fit-content;
}

.select-trigger__clear {
  color: var(--x-back-6);
  font-size: 12px;
  transition: transform 0.2s;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.select-trigger__clear-icon {
  color: var(--x-back-6);
  width: 16px;
  height: 16px;
  &:hover {
    color: var(--x-back-7);
  }
}

.select-trigger__clear:hover {
  color: var(--x-back-8);
}

.select-trigger__arrow {
  color: var(--x-text-2);
  font-size: 12px;
  transition: transform 0.2s;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(90deg);
}

.select-trigger__arrow--active {
  transform: rotate(270deg);
}

.select-trigger__clear {
  display: none;
}

.select-border {
  border: 1px solid var(--x-back-4);
  border-radius: var(--x-radius-medium);
  background: #fff;
  box-shadow: var(--x-shadow);
  overflow: hidden;
}

.select-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  max-height: 228px;
  overflow-x: hidden;
  overflow-y: auto;
}

.select-option {
  padding: 8px 12px;
  cursor: pointer;
  color: var(--x-text-1);
  border-radius: var(--x-radius-medium);
  font-size: 14px;
  line-height: 20px;
  transition: background-color 0.2s;
}

.select-option:hover {
  background-color: var(--x-back-4);
}

.select-option--selected {
  background-color: var(--x-back-4);
  color: var(--x-text-1);
  // font-weight: 600;
}

.select-option--disabled {
  cursor: not-allowed;
  color: var(--x-text-2);
}

.select-option--disabled:hover {
  background-color: transparent;
}

.select-empty {
  padding: 5px 12px;
  color: var(--x-text-2);
  font-size: 14px;
  text-align: center;
}
</style>
